import { API_CONFIG } from '../lib/config';

export type OfferDay = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
export type OfferMeal = 'breakfast' | 'lunch' | 'dinner';
export type OfferItemType = 'breakfast' | 'lunch' | 'dinner' | 'water' | 'drinks' | 'toppings';

export const OFFER_DAYS: OfferDay[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
export const OFFER_MEALS: OfferMeal[] = ['breakfast', 'lunch', 'dinner'];

export interface Offer {
  _id: string;
  name: string;
  slug: string;
  type?: string;
  image?: string;
  description?: string;
  category?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  totalSlots?: number;
  remainingSlots?: number;
  deliveryFeePerMeal?: number;
  standPickupAvailableFrom?: OfferDay;
  pickupEnabled?: boolean;
  pickupInstruction?: string;
  eventDays?: OfferDay[];
  supportPhone?: string;
}

export interface OfferItem {
  _id: string;
  name: string;
  type: OfferItemType;
  price: number;
  image_url?: string;
  description?: string;
}

export interface SlotItem {
  itemId: string;
  itemName?: string;
  quantity: number;
}

export type OfferSchedule = Partial<Record<OfferDay, Partial<Record<OfferMeal, SlotItem[]>>>>;

export interface OfferPlan {
  _id: string;
  name: string;
  description?: string;
  cost: number;
  schedule: OfferSchedule;
  number_of_days?: number;
  caveats?: string;
  additional_info?: string;
  is_popular?: boolean;
}

export interface ManagedOrder {
  _id: string;
  campaign?: { _id: string; name: string; slug: string; description?: string; hotelUpdateDeadline?: string; supportPhone?: string; pickupInstruction?: string };
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  customer_address?: string;
  plan_name?: string;
  schedule: OfferSchedule;
  daily_fulfilment?: DailyFulfilment;
  lodging?: { status?: string; hotel_id?: string; hotel_name?: string; room_number?: string };
  total_amount_paid?: number;
  payment_reference?: string;
  paidAt?: string;
}

export type FulfilmentMode = 'hotel_delivery' | 'stand_pickup';
export type DailyFulfilment = Array<{ day: OfferDay } & Partial<Record<OfferMeal, { mode: FulfilmentMode }>>>;

export interface OfferQuote {
  planMinimum: number;
  selectedTotal: number;
  mealSubtotal: number;
  deliverySubtotal: number;
  charges: number;
  totalAmount: number;
}

export interface OfferOrderRequest {
  campaign: string;
  plan_id: string;
  schedule: OfferSchedule;
  fulfilment_preference: 'all_delivery' | 'all_pickup';
  daily_fulfilment: DailyFulfilment;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address?: string;
  note?: string;
  referral_code?: string;
  callback_url: string;
}

const base = () => API_CONFIG.SUBSCRIPTION_BASE_URL;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${base()}${path}`, {
      ...init,
      headers: { Accept: 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers },
    });
  } catch {
    throw new Error('We could not reach BOQBOX. Check your connection and try again.');
  }
  const json = await response.json().catch(() => null);
  if (!response.ok || json?.success === false) {
    throw Object.assign(new Error(json?.message || 'Something went wrong. Please try again.'), { status: response.status });
  }
  return json as T;
}

export const offersApi = {
  async list(): Promise<Offer[]> {
    return (await request<{ data: Offer[] }>('/campaigns')).data;
  },

  async getBySlug(slug: string): Promise<Offer> {
    return (await request<{ data: Offer }>(`/campaigns/s/${encodeURIComponent(slug)}`)).data;
  },

  async getMenu(offerId: string): Promise<OfferItem[]> {
    const items: OfferItem[] = [];
    let cursor: string | null = null;
    // The endpoint pages at 50; menus are small, but follow the cursor to be safe.
    for (let page = 0; page < 10; page += 1) {
      const query: string = `/campaign/${offerId}/items?limit=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
      const res: { data: OfferItem[]; nextCursor: string | null } = await request(query);
      items.push(...res.data);
      if (!res.nextCursor) break;
      cursor = res.nextCursor;
    }
    return items;
  },

  async getPlans(offerId: string): Promise<OfferPlan[]> {
    const res = await request<{ data: OfferPlan[] }>(`/campaign/${offerId}/plans?limit=50`);
    return res.data.slice().sort((a, b) => a.cost - b.cost);
  },

  async quote(body: Omit<OfferOrderRequest, 'customer_name' | 'customer_email' | 'customer_phone' | 'callback_url'>): Promise<OfferQuote> {
    return (await request<{ data: OfferQuote }>('/campaign/order/quote', { method: 'POST', body: JSON.stringify(body) })).data;
  },

  async createOrder(body: OfferOrderRequest): Promise<{ paymentUrl: string; reference: string; orderId: string }> {
    const res = await request<{ payment_url: string; reference: string; orderId: string }>('/campaign/order/create', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!/^https:\/\/checkout\.paystack\.com\//i.test(res.payment_url || '')) {
      throw new Error('We could not start the payment. Please try again.');
    }
    return { paymentUrl: res.payment_url, reference: res.reference, orderId: String(res.orderId) };
  },

  async getManagedOrder(orderId: string, token: string): Promise<ManagedOrder> {
    return (await request<{ data: ManagedOrder }>(`/campaign/order/${encodeURIComponent(orderId)}?access_token=${encodeURIComponent(token)}`)).data;
  },

  async updateDeliveryDetails(
    orderId: string,
    token: string,
    details: { customer_address?: string; lodging?: { status: 'pending' | 'confirmed'; hotel_name?: string; room_number?: string } }
  ): Promise<void> {
    await request('/campaign/order/lodging/update', { method: 'PATCH', body: JSON.stringify({ orderId, access_token: token, ...details }) });
  },

  async confirmPayment(reference: string): Promise<{ campaign?: string; customer_name?: string; customer_email?: string; total_amount_paid?: number }> {
    return (await request<{ data: any }>(`/confirm-payment?reference=${encodeURIComponent(reference)}`)).data;
  },
};

// ---------- helpers shared by the pages ----------
export const offerPath = (slug: string) => `/offers/${encodeURIComponent(slug)}`;

export const isMainMeal = (type: OfferItemType) => type === 'breakfast' || type === 'lunch' || type === 'dinner';

export const ITEM_GROUPS: Array<{ key: string; label: string; types: OfferItemType[] }> = [
  { key: 'meals', label: 'Meals', types: ['breakfast', 'lunch', 'dinner'] },
  { key: 'drinks', label: 'Drinks', types: ['water', 'drinks'] },
  { key: 'extras', label: 'Extras & toppings', types: ['toppings'] },
];

export const scheduleDays = (schedule: OfferSchedule): OfferDay[] =>
  OFFER_DAYS.filter((day) => OFFER_MEALS.some((meal) => (schedule[day]?.[meal] || []).length));

export const scheduleTotal = (schedule: OfferSchedule, menu: Map<string, OfferItem>): number =>
  scheduleDays(schedule).reduce(
    (sum, day) =>
      sum +
      OFFER_MEALS.reduce(
        (mealSum, meal) =>
          mealSum + (schedule[day]?.[meal] || []).reduce((s, slot) => s + (menu.get(slot.itemId)?.price || 0) * slot.quantity, 0),
        0
      ),
    0
  );

export const isPickupAllowedOn = (offer: Offer, day: OfferDay): boolean => {
  if (offer.pickupEnabled === false) return false;
  const days = offer.eventDays?.length ? offer.eventDays : OFFER_DAYS;
  const start = days.indexOf(offer.standPickupAvailableFrom || 'thursday');
  return start >= 0 && days.indexOf(day) >= start;
};

export const buildDailyFulfilment = (offer: Offer, schedule: OfferSchedule, preference: 'delivery' | 'pickup'): DailyFulfilment =>
  scheduleDays(schedule).map((day) => {
    const mode: FulfilmentMode = preference === 'pickup' && isPickupAllowedOn(offer, day) ? 'stand_pickup' : 'hotel_delivery';
    const entry: DailyFulfilment[number] = { day };
    OFFER_MEALS.forEach((meal) => {
      if ((schedule[day]?.[meal] || []).length) entry[meal] = { mode };
    });
    return entry;
  });

export const titleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const formatDateRange = (start?: string, end?: string): string => {
  const valid = (v?: string) => (v && v !== 'N/A' && !Number.isNaN(new Date(v).getTime()) ? new Date(v) : null);
  const s = valid(start);
  const e = valid(end);
  if (!s) return '';
  const fmt = (d: Date, withYear: boolean) => d.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}) });
  if (!e || s.toDateString() === e.toDateString()) return fmt(s, true);
  return `${fmt(s, s.getFullYear() !== e.getFullYear())} – ${fmt(e, true)}`;
};

// Naira with kobo only when there are kobo (fees make totals fractional): ₦46,377.70, ₦36,600.
export const formatPrice = (amount: number): string => {
  const rounded = Math.round((Number(amount) || 0) * 100) / 100;
  const hasKobo = Math.round(rounded * 100) % 100 !== 0;
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: hasKobo ? 2 : 0, maximumFractionDigits: 2 }).format(rounded);
};
